/**
 * Loading history. `mam.ts` is the wire format this reads off; keeping the
 * query here keeps the envelope layer free of the parser it would otherwise
 * import in a circle.
 */
import type { Client } from "@xmpp/client"
import xml, { type Element as XmlEl } from "@xmpp/xml"
import { nanoid } from "nanoid"
import type { Message } from "../types"
import { NS_DATA_FORM, NS_MAM, NS_RSM } from "./constants"
import type { Jid } from "./jid"
import { parse } from "./stanzas/message"
import { attr } from "./xmlUtils"

export type MamPagingOptions = {
  max?: number
  /** An archive id to page backwards from; omit for the most recent page. */
  before?: string
}

export type MamPage = {
  /** Chronological. */
  messages: Message[]
  /** No older page exists. */
  complete: boolean
  /** Archive id of the oldest row here — the cursor for the next page back. */
  first?: string
  last?: string
}

/**
 * One page of a conversation's history.
 *
 * Results are separate stanzas that arrive BEFORE the IQ reply, so they are
 * collected off the stream under this query's id and the reply only closes the
 * page. The listener is scoped to the request rather than living on the client.
 */
export const loadHistory = async (
  xmpp: Client,
  peer: Jid,
  { max = 50, before = "" }: MamPagingOptions = {}
): Promise<MamPage> => {
  const queryid = `mam-${nanoid(8)}`
  const messages: Message[] = []

  const collect = (stanza: XmlEl) => {
    const result = stanza.getChild("result", NS_MAM)
    if (!result || attr(result, "queryid") !== queryid) return

    const message = parse(stanza)
    if (message) messages.push(message)
  }

  xmpp.on("stanza", collect)
  try {
    const reply = await xmpp.iqCaller.request(
      buildQuery(queryid, peer, max, before)
    )
    const fin = reply.getChild("fin", NS_MAM)
    const set = fin?.getChild("set", NS_RSM)

    return {
      messages,
      complete: attr(fin, "complete") === "true",
      first: set?.getChildText("first") ?? undefined,
      last: set?.getChildText("last") ?? undefined,
    }
  } finally {
    xmpp.off("stanza", collect)
  }
}

/** An empty `<before/>` asks for the last page, which is what we want first. */
const buildQuery = (
  queryid: string,
  peer: Jid,
  max: number,
  before: string
): XmlEl =>
  xml(
    "iq",
    { type: "set" },
    xml(
      "query",
      { xmlns: NS_MAM, queryid },
      xml(
        "x",
        { xmlns: NS_DATA_FORM, type: "submit" },
        xml(
          "field",
          { var: "FORM_TYPE", type: "hidden" },
          xml("value", {}, NS_MAM)
        ),
        xml("field", { var: "with" }, xml("value", {}, peer))
      ),
      xml(
        "set",
        { xmlns: NS_RSM },
        xml("max", {}, String(max)),
        xml("before", {}, before)
      )
    )
  )
